import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Activity, ArrowLeft, ArrowRight, Download, History, Users } from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface LoginSessionRow {
  id: number;
  username: string;
  date?: string;
  loginAt: string | null;
  lastSeenAt: string | null;
  expiresAt: string | null;
  logoutAt: string | null;
  logoutReason?: string | null;
  isActive: boolean;
  statusLabel?: string;
}

interface TrendRow {
  date?: string;
  month?: string;
  label: string;
  logins: number;
  logouts: number;
}

interface LoginActivityResponse {
  rangeStart: string;
  rangeEnd: string;
  currentLoggedInCount: number;
  currentLoggedInUsers: LoginSessionRow[];
  totalLoginHistory: LoginSessionRow[];
  uniqueLoginHistory: LoginSessionRow[];
  totalDailyTrend: TrendRow[];
  uniqueDailyTrend: TrendRow[];
  totalMonthlyTrend: TrendRow[];
  uniqueMonthlyTrend: TrendRow[];
}

const extractErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const formatDateKey = (value: Date) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseUtcDate = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const normalized = /z$|[+-]\d{2}:\d{2}$/i.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '未取得';
  }
  const parsed = parseUtcDate(value);
  if (!parsed) {
    return '未取得';
  }
  return parsed.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

const createDefaultRangeStart = () => {
  const today = startOfDay(new Date());
  today.setDate(today.getDate() - 13);
  return formatDateKey(today);
};

interface LoginActivityDashboardProps {
  onBackToDeveloper: () => void;
}

const LoginActivityDashboard: React.FC<LoginActivityDashboardProps> = ({ onBackToDeveloper }) => {
  const [rangeStart, setRangeStart] = useState(createDefaultRangeStart);
  const [data, setData] = useState<LoginActivityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [historyMode, setHistoryMode] = useState<'total' | 'unique'>('total');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<LoginActivityResponse>(API_ENDPOINTS.adminLoginActivity, {
        params: { rangeStart },
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'ログイン状況の取得に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  }, [rangeStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMoveRange = (days: number) => {
    const next = new Date(`${rangeStart}T00:00:00`);
    next.setDate(next.getDate() + days);
    setRangeStart(formatDateKey(next));
  };

  const handleDownloadCsv = async () => {
    setIsDownloading(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.adminLoginActivityCsv, {
        params: { rangeStart },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user-login-trend-${rangeStart}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractErrorMessage(err, 'CSV のダウンロードに失敗しました。'));
    } finally {
      setIsDownloading(false);
    }
  };

  const canMoveNext = useMemo(() => {
    const maxStart = startOfDay(new Date());
    maxStart.setDate(maxStart.getDate() - 13);
    return new Date(`${rangeStart}T00:00:00`).getTime() < maxStart.getTime();
  }, [rangeStart]);

  const currentActiveCount = data?.currentLoggedInUsers.length || 0;
  const rangeLoginCount = useMemo(() => (data?.totalDailyTrend || []).reduce((sum, row) => sum + row.logins, 0), [data]);
  const rangeUniqueCount = useMemo(() => (data?.uniqueDailyTrend || []).reduce((sum, row) => sum + row.logins, 0), [data]);
  const monthlyLoginCount = useMemo(() => (data?.totalMonthlyTrend || []).reduce((sum, row) => sum + row.logins, 0), [data]);
  const monthlyUniqueCount = useMemo(() => (data?.uniqueMonthlyTrend || []).reduce((sum, row) => sum + row.logins, 0), [data]);
  const selectedHistory = historyMode === 'total' ? (data?.totalLoginHistory || []) : (data?.uniqueLoginHistory || []);
  const selectedDailyTrend = historyMode === 'total' ? (data?.totalDailyTrend || []) : (data?.uniqueDailyTrend || []);
  const selectedMonthlyTrend = historyMode === 'total' ? (data?.totalMonthlyTrend || []) : (data?.uniqueMonthlyTrend || []);

  if (isLoading) {
    return (
      <div className="file-manager-loading">
        <div className="loading-spinner"></div>
        <p>ユーザログイン状況を読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="login-activity-page">
      <section className="login-activity-hero">
        <div>
          <span className="login-activity-eyebrow">Admin Visibility</span>
          <h2>ユーザログイン状況</h2>
          <p>一般ユーザのログインセッションを DB で追跡し、現在のログイン状況と推移を確認できます。</p>
        </div>
        <div className="login-activity-hero-actions">
          <button className="login-activity-back-button" onClick={onBackToDeveloper}>
            <ArrowLeft size={16} />
            管理者コンソールに戻る
          </button>
          <button className="login-activity-download" onClick={handleDownloadCsv} disabled={isDownloading}>
            <Download size={16} />
            {isDownloading ? 'CSV 生成中...' : 'ログイン推移をCSVダウンロード'}
          </button>
        </div>
      </section>

      {error && (
        <div className="error-message">
          <span>{error}</span>
        </div>
      )}

      <section className="login-activity-summary-grid">
        <article className="login-summary-card">
          <div className="login-summary-icon">
            <Users size={18} />
          </div>
          <div>
            <span>現在ログイン中</span>
            <strong>{currentActiveCount} 件</strong>
          </div>
        </article>
        <article className="login-summary-card">
          <div className="login-summary-icon">
            <History size={18} />
          </div>
          <div>
            <span>対象2週間の延べ / ユニーク</span>
            <strong>{rangeLoginCount} / {rangeUniqueCount} 件</strong>
          </div>
        </article>
        <article className="login-summary-card">
          <div className="login-summary-icon">
            <Activity size={18} />
          </div>
          <div>
            <span>対象2か月の延べ / ユニーク</span>
            <strong>{monthlyLoginCount} / {monthlyUniqueCount} 件</strong>
          </div>
        </article>
      </section>

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>現在ログイン中の一般ユーザ</h3>
            <p>未ログアウトかつ有効期限内のセッションを表示しています。</p>
          </div>
        </div>

        <div className="login-activity-table-wrap">
          <table className="login-activity-table">
            <thead>
              <tr>
                <th>ログインユーザ名</th>
                <th>ログイン日時</th>
                <th>最終アクセス</th>
                <th>ログアウト日時</th>
              </tr>
            </thead>
            <tbody>
              {data?.currentLoggedInUsers.length ? (
                data.currentLoggedInUsers.map((row) => (
                  <tr key={`current-${row.id}`}>
                    <td>{row.username}</td>
                    <td>{formatDateTime(row.loginAt)}</td>
                    <td>{formatDateTime(row.lastSeenAt)}</td>
                    <td>{formatDateTime(row.logoutAt)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="login-activity-empty-cell">現在ログイン中の一般ユーザはいません。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>{historyMode === 'total' ? '延べ訪問者' : 'ユニーク訪問者'}</h3>
            <p>{data?.rangeStart} から {data?.rangeEnd} までの 2 週間を確認できます。</p>
          </div>
          <div className="login-activity-panel-actions">
            <div className="login-activity-toggle-group">
              <button
                className={`login-toggle-button ${historyMode === 'total' ? 'active' : ''}`}
                onClick={() => setHistoryMode('total')}
              >
                延べ訪問者
              </button>
              <button
                className={`login-toggle-button ${historyMode === 'unique' ? 'active' : ''}`}
                onClick={() => setHistoryMode('unique')}
              >
                ユニーク訪問者
              </button>
            </div>
            <div className="login-activity-range-controls">
              <button className="login-range-button" onClick={() => handleMoveRange(-14)}>
                <ArrowLeft size={16} />
                前の2週間
              </button>
              <button className="login-range-button" onClick={() => handleMoveRange(14)} disabled={!canMoveNext}>
                次の2週間
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="login-activity-table-wrap">
          <table className="login-activity-table">
            <thead>
              <tr>
                <th>ログインユーザ名</th>
                <th>ログイン日時</th>
                <th>ログアウト日時</th>
                <th>状態</th>
              </tr>
            </thead>
            <tbody>
              {selectedHistory.length ? (
                selectedHistory.map((row) => (
                  <tr key={row.id}>
                    <td>{row.username}</td>
                    <td>{formatDateTime(row.loginAt)}</td>
                    <td>{formatDateTime(row.logoutAt)}</td>
                    <td>{row.statusLabel || (row.isActive ? 'ログイン中' : (row.logoutReason === 'expired' ? '期限切れ' : '終了'))}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="login-activity-empty-cell">対象期間の訪問履歴はありません。</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="login-activity-chart-grid">
        <article className="login-activity-panel">
          <div className="login-activity-panel-header">
            <div>
              <h3>ログイン推移（{historyMode === 'total' ? '延べ訪問者' : 'ユニーク訪問者'}）</h3>
              <p>{historyMode === 'total' ? 'ログイン回数とログアウト回数を表示します。' : 'ユーザ重複を除いた訪問者数を表示します。'}</p>
            </div>
          </div>
          <div className="login-chart-area">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={selectedDailyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#dbe3ef" />
                <XAxis dataKey="label" stroke="#5b6472" />
                <YAxis allowDecimals={false} stroke="#5b6472" />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="logins" name={historyMode === 'total' ? 'ログイン' : 'ユニーク訪問者'} stroke="#0f766e" strokeWidth={3} />
                {historyMode === 'total' && (
                  <Line type="monotone" dataKey="logouts" name="ログアウト" stroke="#dc2626" strokeWidth={2} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="login-activity-panel">
          <div className="login-activity-panel-header">
            <div>
              <h3>月別推移（2か月）</h3>
              <p>{historyMode === 'total' ? '月単位のログイン回数とログアウト回数を確認できます。' : '月単位のユニーク訪問者数を確認できます。'}</p>
            </div>
          </div>
          <div className="login-chart-area">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={selectedMonthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#dbe3ef" />
                <XAxis dataKey="label" stroke="#5b6472" />
                <YAxis allowDecimals={false} stroke="#5b6472" />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="logins" name={historyMode === 'total' ? 'ログイン' : 'ユニーク訪問者'} stroke="#1d4ed8" strokeWidth={3} />
                {historyMode === 'total' && (
                  <Line type="monotone" dataKey="logouts" name="ログアウト" stroke="#ea580c" strokeWidth={2} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>
      </section>
    </div>
  );
};

export default LoginActivityDashboard;
